import { isInsideOnboardingAtom, isLoggedInAtom, isStartingAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import {
  LogoCanada,
  LogoHauteGaspesie,
  LogoMutuoSplash,
  LogoRefrainCarre,
  LogoSerresSplash,
  LogoTwoLines,
} from 'app/design/branding/logo';
import { images } from 'app/design/images';
import { Column } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { env } from 'app/lib/env';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isDarkColor, isFeatureDisabled, isWhiteLabel } from 'app/utils/helpers';
import { isGuestAccessEnabled, useUISize } from 'app/utils/web-helpers';
import { StatusBar } from 'expo-status-bar';
import { useAtomValue } from 'jotai/index';
import React, { useEffect, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { DefaultSplashLogo } from './default-splash-logo';
import { SPLASH_ENTRANCE_DURATION_MS } from './splash.constants';

function WhiteLabelSplash() {
  const { t } = useScopedTranslation();
  const { isSmallUI } = useUISize();
  const w = useDeviceWidth();
  const theme = useTheme();
  const isRefrain = isWhiteLabel(WhiteLabel.Refrain);
  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const shouldCenterHauteGaspesieSplash = isSmallUI && isHauteGaspesie;

  return (
    <>
      <Animated.View entering={FadeInUp.duration(SPLASH_ENTRANCE_DURATION_MS)} exiting={FadeOutUp.duration(500)}>
        {isRefrain ? (
          <View style={{ alignItems: 'center', width: '100%', gap: theme.spacing['spacing.general.x2'] }}>
            <LogoRefrainCarre height={(w * 220) / 380} width={w} />
            <Text
              fontSize={11}
              numberOfLines={2}
              width={0.75 * w}
              textAlign="center"
              style={{ color: 'white' }}
              variant="bodyBase"
            >
              {t('title')}
            </Text>
            <LogoCanada height={((w / 5) * 218) / 918} width={w / 5} />
          </View>
        ) : undefined}
        {isWhiteLabel(WhiteLabel.SerresTessier) ? (
          <LogoSerresSplash height={(w * 0.7 * 701) / 576} width={w * 0.7} />
        ) : undefined}
        {isHauteGaspesie ? (
          <View
            style={{
              alignItems: 'center',
              width: '100%',
              gap: theme.spacing['spacing.general.x6'],
              ...(!shouldCenterHauteGaspesieSplash && { bottom: 220 }),
            }}
          >
            <LogoHauteGaspesie height={(w * 0.7 * 48) / 90} width={w * 0.7} />
          </View>
        ) : undefined}
        {isWhiteLabel(WhiteLabel.Mutuo) ? (
          <View style={{ alignItems: 'center', width: '100%', gap: theme.spacing['spacing.general.x2'] }}>
            <LogoMutuoSplash height={(w * 0.7 * 260) / 576} width={w * 0.7} />
            <Text
              fontSize={11}
              numberOfLines={2}
              width={0.75 * w}
              textAlign="center"
              style={{ color: 'white', marginTop: theme.spacing['spacing.general.x12'] }}
              variant="bodyBase"
            >
              {t('title')}
            </Text>
            <PCImage source={images.partners} height={((w / 3) * 128) / 373} width={w / 3} />
          </View>
        ) : undefined}
      </Animated.View>
      <Column
        style={{
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'center',
          margin: theme.spacing['spacing.general.x12'],
          ...(isWhiteLabel(WhiteLabel.Mutuo) && { bottom: 0 }),
          ...((isRefrain || shouldCenterHauteGaspesieSplash) && {
            position: 'relative',
            margin: 0,
            marginTop: theme.spacing['spacing.general.x12'],
          }),
        }}
      >
        <Text fontSize={20} style={{ color: 'white' }} variant="bodyBase">
          {t('poweredBy')}
        </Text>
        <View style={isHauteGaspesie ? { marginTop: -24 } : undefined}>
          <LogoTwoLines color="white" height={132} width={132} />
        </View>
      </Column>
    </>
  );
}

const SplashScreenComponent: NavigationScreen<'splash'> = ({ navigation }) => {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const { isLargeUI } = useUISize();
  const splashMountedAt = useRef<number | null>(null);

  useEffect(() => {
    splashMountedAt.current = Date.now();
  }, []);

  useEffect(() => {
    if (isStarting) return;

    const goToNextScreen = () => {
      if (isInsideOnboarding) return;

      const guestAccessEnabled = Platform.OS === 'web' && isGuestAccessEnabled();
      if (isLoggedIn || guestAccessEnabled || isFeatureDisabled(FeatureFlag.useNewOnboarding)) {
        navigation.reset({ routes: [{ name: 'main' }] });
      } else {
        if (isLargeUI) {
          navigation.navigate('onboardingIntro', undefined, { pop: true });
        } else {
          navigation.reset({ routes: [{ name: 'presentPartageClub' }] });
        }
      }
    };

    // Do not leave before the entrance animation has had time to play. If
    // startup took longer than the animation, this remains an immediate transition.
    const remainingAnimationTime = Math.max(
      0,
      SPLASH_ENTRANCE_DURATION_MS - (Date.now() - (splashMountedAt.current ?? Date.now())),
    );
    const timeout = setTimeout(goToNextScreen, remainingAnimationTime);

    return () => clearTimeout(timeout);
  }, [isInsideOnboarding, isLargeUI, isLoggedIn, isStarting, navigation]);

  const theme = useTheme();

  // Old condition check same value between both color and always run first condition ?... So
  // So we can simplify and directly use
  const isStatusbarDark = isDarkColor(theme.colors['gradient.primary']); // or theme.colors.page ???

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors['white-label.splash'] }]}>
      <StatusBar style={isStatusbarDark ? 'dark' : 'light'} />
      {isWhiteLabel() ? <WhiteLabelSplash /> : env.IS_PARTAKE ? <DefaultSplashLogo /> : <DefaultSplashLogo />}
    </SafeAreaView>
  );
};

export const SplashScreen: NavigationScreen<'splash'> = withErrorBoundary(SplashScreenComponent);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
